/**
 * CollectorPriceView - zakladka cen specjalnych: skanowanie, wyswietlanie
 * produktu, edycja ceny, dodawanie do bufora cenowek.
 * Wyodrebniony z KolektorPage.jsx.
 */

import React from 'react';
import BarcodeScanner from './BarcodeScanner';
import { ScannerErrorBoundary } from './CollectorScanner';

const CollectorPriceView = ({
  // Price scanning
  priceScanMode, setPriceScanMode,
  isPriceScannerActive, setIsPriceScannerActive,
  priceScannedProduct,
  priceEditValue, setPriceEditValue,
  priceBuffer,
  // Manual EAN
  manualEan, setManualEan,
  // Price search
  priceSearchQuery,
  priceSearchResults,
  priceSearchLoading,
  // Handlers
  handlePriceScan,
  handlePriceSearch,
  handleSelectPriceProduct,
  handleUpdatePrice,
  handleAddToBuffer,
  handleClearPriceProduct,
  setError,
  loading,
}) => {
  // Koszt zakupu spojny z wyswietlana marza: gdy FIFO dostepne pokazujemy
  // cene zakupu FIFO (netto z partii; brutto = netto*(1+VAT), dla kaucji/VAT<=0
  // brutto=netto), inaczej fallback na karte produktu. Marza i koszt na
  // ekranie pochodza z tej samej pary.
  const p = priceScannedProduct;
  const hasFifo = p?.fifo_purchase_price != null;
  const vatRate = Number(p?.stawka_vat) > 0 ? Number(p.stawka_vat) : 0;
  const zakupNetto = hasFifo ? p.fifo_purchase_price : p?.cena_zakupu_netto;
  const zakupBrutto = hasFifo
    ? p.fifo_purchase_price * (1 + vatRate / 100)
    : p?.cena_zakupu_brutto;
  const displayMarginPercent = hasFifo ? p?.fifo_margin_percent : p?.margin_percent;
  const displayMarginAmount = hasFifo ? p?.fifo_margin_amount : p?.margin_amount;
  const isKaucja = !!p?.is_kaucja;

  return (
  <div>
    {/* Skaner cen */}
    {!priceScannedProduct && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <h6 style={{ margin: '0 0 1rem 0', fontSize: '14px', fontWeight: '600', color: 'var(--success)' }}>
          Znajdz produkt aby sprawdzic/zmienic cene
        </h6>

        {/* Przelacznik trybu */}
        <div style={{
          display: 'flex',
          gap: '0.25rem',
          marginBottom: '1rem',
          backgroundColor: 'var(--border)',
          padding: '3px',
          borderRadius: '6px'
        }}>
          <button
            onClick={() => { setPriceScanMode('camera'); setIsPriceScannerActive(true); }}
            style={{
              flex: 1,
              padding: '6px 12px',
              backgroundColor: priceScanMode === 'camera' ? 'var(--card-bg)' : 'transparent',
              color: priceScanMode === 'camera' ? 'var(--success)' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: priceScanMode === 'camera' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            Kamera
          </button>
          <button
            onClick={() => { setPriceScanMode('manual'); setIsPriceScannerActive(false); }}
            style={{
              flex: 1,
              padding: '6px 12px',
              backgroundColor: priceScanMode === 'manual' ? 'var(--card-bg)' : 'transparent',
              color: priceScanMode === 'manual' ? 'var(--success)' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: priceScanMode === 'manual' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            EAN
          </button>
          <button
            onClick={() => { setPriceScanMode('search'); setIsPriceScannerActive(false); }}
            style={{
              flex: 1,
              padding: '6px 12px',
              backgroundColor: priceScanMode === 'search' ? 'var(--card-bg)' : 'transparent',
              color: priceScanMode === 'search' ? 'var(--success)' : 'var(--text-muted)',
              fontSize: '12px',
              fontWeight: priceScanMode === 'search' ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            Szukaj
          </button>
        </div>

        {/* Tryb kamery */}
        {priceScanMode === 'camera' && (
          <ScannerErrorBoundary>
            <BarcodeScanner
              isActive={isPriceScannerActive}
              onScan={handlePriceScan}
              onError={(err) => setError(err)}
            />
          </ScannerErrorBoundary>
        )}

        {/* Tryb reczny EAN */}
        {priceScanMode === 'manual' && (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                placeholder="Wpisz kod EAN..."
                value={manualEan}
                onChange={(e) => setManualEan(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && manualEan.trim()) {
                    handlePriceScan(manualEan.trim());
                    setManualEan('');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  border: '2px solid var(--success)',
                  borderRadius: '6px',
                  fontSize: '18px',
                  textAlign: 'center'
                }}
                autoFocus
              />
              <button
                onClick={() => {
                  if (manualEan.trim()) {
                    handlePriceScan(manualEan.trim());
                    setManualEan('');
                  }
                }}
                style={{
                  padding: '6px 12px',
                  border: '1px solid var(--success)',
                  color: 'var(--success)',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                &#8594;
              </button>
            </div>
          </div>
        )}

        {/* Tryb wyszukiwania */}
        {priceScanMode === 'search' && (
          <div>
            <input
              type="text"
              placeholder="Wpisz nazwe produktu..."
              value={priceSearchQuery}
              onChange={(e) => handlePriceSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '0.875rem',
                border: '2px solid var(--success)',
                borderRadius: '6px',
                fontSize: '16px',
                marginBottom: '0.5rem'
              }}
              autoFocus
            />

            {/* Loading */}
            {priceSearchLoading && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                Szukam...
              </div>
            )}

            {/* Wyniki wyszukiwania */}
            {priceSearchResults.length > 0 && (
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid var(--border)',
                borderRadius: '6px'
              }}>
                {priceSearchResults.map((product, index) => (
                  <div
                    key={product.id || index}
                    onClick={() => handleSelectPriceProduct(product)}
                    style={{
                      padding: '0.75rem',
                      borderBottom: index < priceSearchResults.length - 1 ? '1px solid var(--border)' : 'none',
                      cursor: 'pointer',
                      backgroundColor: 'var(--card-bg)'
                    }}
                  >
                    <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>
                      {product.nazwa || product.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {product.barcode || product.ean || product.kod_produktu} | Stan: {product.stock_quantity ?? product.stock ?? product.stan ?? 0} | {(product.price || product.cena_sprzedazy_brutto || product.cena || 0).toFixed(2)} zl
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Brak wynikow */}
            {!priceSearchLoading && priceSearchQuery.length >= 2 && priceSearchResults.length === 0 && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '13px' }}>
                Nie znaleziono produktow
              </div>
            )}

            {/* Podpowiedz */}
            {priceSearchQuery.length < 2 && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '12px' }}>
                Wpisz min. 2 znaki aby wyszukac
              </div>
            )}
          </div>
        )}
      </div>
    )}

    {/* Wyswietlenie zeskanowanego produktu */}
    {priceScannedProduct && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        borderRadius: '8px',
        padding: '1rem',
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Naglowek produktu */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1rem'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>
              {priceScannedProduct.name}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              EAN: {priceScannedProduct.ean} | Stan: {priceScannedProduct.stock}
            </div>
          </div>
          <button
            onClick={handleClearPriceProduct}
            style={{
              padding: '4px 10px',
              backgroundColor: 'var(--border)',
              cursor: 'pointer',
              fontSize: '11px'
            }}
          >
            &#10005; Zamknij
          </button>
        </div>

        {/* Informacje o cenach */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '0.75rem',
          marginBottom: '1rem',
          padding: '0.75rem',
          backgroundColor: 'var(--bg)',
          borderRadius: '6px'
        }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>
              {hasFifo ? 'Zakup (FIFO) netto' : 'Zakup (karta) netto'}
            </div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>
              {zakupNetto?.toFixed(2) || '0.00'} zl
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>
              {hasFifo ? 'Zakup (FIFO) brutto' : 'Zakup (karta) brutto'}
            </div>
            <div style={{ fontWeight: '600', fontSize: '14px' }}>
              {zakupBrutto?.toFixed(2) || '0.00'} zl
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>
              {hasFifo ? 'Marza FIFO' : 'Marza (karta)'}
            </div>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: isKaucja ? 'var(--text-muted)' :
                     displayMarginPercent >= 20 ? 'var(--success)' :
                     displayMarginPercent >= 10 ? 'var(--warning)' : 'var(--danger)'
            }}>
              {isKaucja ? 'kaucja' : `${displayMarginPercent?.toFixed(1) || '0.0'}%`}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Marza ost.fakt.</div>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: isKaucja ? 'var(--text-muted)' :
                     priceScannedProduct.margin_percent >= 20 ? 'var(--success)' :
                     priceScannedProduct.margin_percent >= 10 ? 'var(--warning)' : 'var(--danger)'
            }}>
              {isKaucja ? 'kaucja' : `${priceScannedProduct.margin_percent?.toFixed(1) || '0.0'}%`}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>Marza kwota</div>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: isKaucja ? 'var(--text-muted)' :
                     displayMarginAmount >= 0 ? 'var(--success)' : 'var(--danger)'
            }}>
              {isKaucja ? 'kaucja' : `${displayMarginAmount?.toFixed(2) || '0.00'} zl`}
            </div>
          </div>
        </div>

        {/* Aktualna cena sprzedazy */}
        <div style={{
          padding: '0.75rem',
          backgroundColor: priceScannedProduct.has_special_price ? 'var(--success-light)' : 'var(--info-light)',
          borderRadius: '6px',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            {priceScannedProduct.has_special_price ? 'Cena specjalna' : 'Cena standardowa'}
          </div>
          <div style={{ fontWeight: '700', fontSize: '24px', color: 'var(--success)' }}>
            {priceScannedProduct.cena_sprzedazy_brutto?.toFixed(2) || '0.00'} zl
          </div>
        </div>

        {/* Edycja ceny */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px', display: 'block' }}>
            Nowa cena brutto:
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
            <input
              type="number"
              step="0.01"
              value={priceEditValue}
              onChange={(e) => setPriceEditValue(e.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '0.75rem',
                border: '2px solid var(--success)',
                borderRadius: '6px',
                fontSize: '18px',
                fontWeight: '600',
                textAlign: 'center'
              }}
            />
            <button
              onClick={handleUpdatePrice}
              disabled={loading}
              style={{
                flexShrink: 0,
                padding: '6px 12px',
                border: '1px solid var(--success)',
                color: 'var(--success)',
                fontSize: '12px',
                fontWeight: '600',
                opacity: loading ? 0.6 : 1,
                whiteSpace: 'nowrap'
              }}
            >
              {loading ? '...' : 'Zapisz'}
            </button>
          </div>
        </div>

        {/* Przycisk dodania do bufora */}
        <button
          onClick={handleAddToBuffer}
          style={{
            width: '100%',
            padding: '6px 12px',
            border: '1px solid var(--orange)',
            color: 'var(--orange)',
            fontWeight: '600',
            fontSize: '12px'
          }}
        >
          Dodaj do bufora cenowek ({priceBuffer.length})
        </button>
      </div>
    )}
  </div>
  );
};

export default CollectorPriceView;
